import api from '../../../services/apiConfig';

// --- Types ---

export interface ComplaintStatus {
    id: string;
    label: string;
    color: string; // Hex code or Tailwind class mapping
}

export interface ComplaintItem {
    id: string;
    productId: number;
    productRef: string;
    productDesc: string;
    ca?: number;
    batch: string; // "Lote/Fabricação"
    observation: string;
    quantities: { [size: string]: number }; // Size -> Qty (User input)
    originalQuantities: { [size: string]: number }; // Size -> Qty (From Order)
    totalPurchased: number;
}

export interface Complaint {
    id: string;
    orderId: number;
    orderCode: string; // COD_TEOREMA
    clientName: string;
    clientCnpj: string;
    dateOpened: string;
    status: 'open' | 'analysis' | 'completed';
    items: ComplaintItem[];
    images: string[]; // URLs or base64
    finalObservation?: string;

}

// Data for Order Search (Step 1)
export interface OrderSearchResult {
    id: number;
    code: string; // COD_TEOREMA
    clientId: number;
    clientName: string;
    clientCnpj: string;
    emissionDate: string;
    billingDate: string | null;
    segment?: string; // Segmento do vendedor
    totalValue?: number; // Valor total do pedido
}

// Data for Order Details (Step 2 - Items available in the order)
export interface OrderItemDetails {
    productId: number;
    reference: string;
    description: string;
    ca?: number;
    sizes: { [size: string]: number }; // Size -> Qty Purchased
    totalPurchased: number;
}

// --- Interfaces da API Backend ---

export interface ComplaintListItem {
    id: number;
    vendaId: number;
    clienteId: number;
    vendedorId: number;
    segmento: string | null;
    situacao: 'ABERTA' | 'EM_ANALISE' | 'CONCLUIDA' | 'PENDENTE' | 'REJEITADA';
    dataAbertura: string;
    numeroControle: string | null;

    protocoloSac: string | null;
    parecerSac: string | null;
    tipoRessarcimento: 'CREDITO' | 'DESCONTO' | 'REPOSICAO' | 'NENHUM';
    clienteNome: string;
    pedidoCodigo: string;
    qcAutorizado?: string | null;
}

export interface ComplaintDetailItem {
    id: number;
    produtoId: number;
    referencia: string;
    descricao: string;
    ca: number | null;
    lote: string | null;
    observacao: string | null;
    paresComprados: number;
    quantidades: Record<string, number>;
}

export interface ComplaintDetail extends ComplaintListItem {
    parecerSac: string | null;
    clienteDocumento: string;
    pedidoEmissao: string;
    fotosUrls: string[]; // JSON parsed from DB
    etiquetasUrls: string[]; // JSON parsed from DB
    valorReposicao: number | null;
    infoReposicao: string | null;
    qcInfoReposicao: string | null;
    items: ComplaintDetailItem[];
}

export interface ComplaintsApiResponse {
    data: ComplaintListItem[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    counts?: {
        ABERTA: number;
        EM_ANALISE: number;
        CONCLUIDA: number;
        PENDENTE: number;
    };
}

// --- API Functions ---

/**
 * Busca lista de reclamações do vendedor logado (paginada)
 */
export const fetchComplaints = async (
    page = 1,
    limit = 50,
    status?: string | null,
    search?: string | null,
    startDate?: string | null,
    endDate?: string | null,
    tipoAutorizacao?: string | null
): Promise<ComplaintsApiResponse> => {
    const params: any = { page, limit };
    if (status) params.situacao = status;
    if (search) params.search = search;
    if (startDate) params.dataInicio = startDate;
    if (endDate) params.dataFim = endDate;
    if (tipoAutorizacao) params.tipoAutorizacao = tipoAutorizacao;

    const response = await api.get<ComplaintsApiResponse>('/complaints', {
        params
    });
    return response.data;
};

/**
 * Busca detalhes de uma reclamação específica com itens
 */
export const fetchComplaintById = async (id: number): Promise<ComplaintDetail> => {
    const response = await api.get<ComplaintDetail>(`/complaints/${id}`);
    return response.data;
};

export const searchOrders = async (query: string): Promise<OrderSearchResult[]> => {
    if (!query) return [];

    try {
        const response = await api.get<OrderSearchResult[]>(`/complaints/search-orders`, {
            params: { search: query }
        });
        const data = response.data;
        // Guard: if backend wraps in { data: [...] }
        if (Array.isArray(data)) return data;
        if (data && Array.isArray((data as any).data)) return (data as any).data;
        console.warn("[searchOrders] Resposta inesperada:", data);
        return [];
    } catch (error: any) {
        console.error("Erro ao buscar pedidos reais:", error?.response?.status, error?.response?.data || error.message);
        return [];
    }
};

export const fetchOrderItems = async (orderId: number): Promise<OrderItemDetails[]> => {
    try {
        const response = await api.get<OrderItemDetails[]>(`/complaints/order-items/${orderId}`);
        return response.data;
    } catch (error) {
        console.error("Erro ao buscar itens reais do pedido:", error);
        return [];
    }
};

// --- Interfaces para criação de reclamação ---

export interface CreateComplaintItemPayload {
    produtoId: number;
    referencia: string;
    descricao: string;
    ca: number | null;
    lote: string | null;
    observacao: string | null;
    paresComprados: number;
    quantidades: Record<string, number>;
}

export interface CreateComplaintPayload {
    vendaId: number;
    clienteId: number;
    vendedorId: number;
    segmento: string | null;
    tipoRessarcimento: 'CREDITO' | 'DESCONTO' | 'REPOSICAO' | 'NENHUM';
    totalImages: number;
    items: CreateComplaintItemPayload[];
}

export interface CreateComplaintResponse {
    id: number;
    message: string;
}

export const createComplaint = async (payload: CreateComplaintPayload): Promise<CreateComplaintResponse> => {
    const response = await api.post<CreateComplaintResponse>('/complaints', payload);
    return response.data;
};

export interface UploadImageResponse {
    message: string;
    url: string;
    urls: string[];
}

export const uploadComplaintImage = async (id: number, file: File, type: 'foto' | 'etiqueta' = 'foto'): Promise<UploadImageResponse> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post<UploadImageResponse>(`/complaints/${id}/images?type=${type}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

// --- Controle de Qualidade / Reposição ---

export interface QualityControlItem {
    id: number;
    dataReclamacao: string;
    referencia: string | null;
    lote: string | null;
    tamanho: string | null;
    quantidade: number | null;
    descricaoDefeito: string | null;
    modoFalha: string | null;
    causa: string | null;
    gravidade: 'BAIXA' | 'MÉDIA' | 'ALTA' | 'CRÍTICA' | null;
    natureza: string | null;
    procedencia: 'KADESH' | 'IMBISEG' | 'OUTRO' | null;
    naoConformidade: string | null;
    supervisor: string | null;
    segmento: string | null;
    clienteCidade: string | null;
    autorizado: string;
    infoReposicao: string | null;
    reclamacaoId: number | null;
    itemId: number | null;
    valorReposicao: number | null;
    createdAt: string;
    updatedAt: string;
    itemReferencia: string | null;
    itemDescricao: string | null;
}

export const fetchQualityControl = async (complaintId: number): Promise<QualityControlItem[]> => {
    const response = await api.get<QualityControlItem[]>(`/complaints/${complaintId}/quality`);
    return response.data;
};

export interface UpdateReposicaoPayload {
    valorReposicao?: number | null;
    infoReposicao?: string | null;
}

export const updateComplaintReposicao = async (complaintId: number, payload: UpdateReposicaoPayload): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/complaints/${complaintId}/reposicao`, payload);
    return response.data;
};

/**
 * Busca a lista de tipos de autorização únicos do CQ
 */
export const fetchAuthorizationTypes = async (): Promise<string[]> => {
    const response = await api.get<string[]>('/complaints/authorization-types');
    return response.data;
};
